/**
 * TaskAssignmentFields Component
 *
 * Handles request details and assignment:
 * - Assignee Org / User (for platform without project)
 * - Channel Partner
 * - Advertiser
 *
 * Extracted from TaskForm.js for maintainability.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Card, Col, Row, Form, Select } from 'antd';
import Text from 'antd/lib/typography/Text';
import { startCase } from 'lodash';

import '../../styles/index.scss';

const { Option } = Select;

const TaskAssignmentFields = ({
  form,
  level,
  currentTaskId,
  currentTask,
  selectedProjectId,
  userOrgName,
  assigneeOrgs,
  assigneeUsers,
  channelPartnerOrgs,
  advertiserOrgs,
  basicOrgsDetailsLoading,
  basicUsersDetailsLoading,
  fetchBasicOrgs,
  fetchBasicUsers,
  setShowCollaboratorModal,
  setSelectedCollaboratorId,
  setShowFollowerModal,
  setSelectedFollowerId,
  teams,
  teamsLoading,
  selectedTeamId,
  setSelectedTeamId,
  fetchTeamsByOrg,
}) => {
  // Filter assignee users by selected team members
  const filteredAssigneeUsers = selectedTeamId
    ? (() => {
        const selectedTeam = (teams || []).find((t) => t.id === selectedTeamId);
        if (!selectedTeam?.members) return assigneeUsers;
        const memberIds = selectedTeam.members.map((m) => m.id);
        return assigneeUsers.filter((u) => memberIds.includes(u.id));
      })()
    : assigneeUsers;
  return (
    <Col className="task-assignment-fields">
      <Card
        className="task-assignment-fields__card"
        title={
          <Text className="task-assignment-fields__card-title">REQUEST DETAILS</Text>
        }
      >
        {/* Row 1: Assignee Org and User (Platform only, no project) */}
        <Row gutter={32}>
          {level === 'PLATFORM' && !selectedProjectId && (
            <>
              <Col xs={24} sm={24} md={12} lg={12}>
                <Form.Item
                  name="assigneeOrgId"
                  label={
                    <div>
                      <div>Assignee Org</div>
                      <div className="task-assignment-fields__label-hint">
                        The org that will receive this task
                      </div>
                    </div>
                  }
                  colon={false}
                >
                  <Select
                    allowClear
                    showSearch
                    placeholder="Select Assignee Org"
                    loading={basicOrgsDetailsLoading}
                    onFocus={() => fetchBasicOrgs()}
                    onSearch={(searchText) => fetchBasicOrgs(searchText)}
                    filterOption={(input, option) =>
                      option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                    filterSort={(optionA, optionB) =>
                      optionA.children
                        .toLowerCase()
                        .localeCompare(optionB.children.toLowerCase())
                    }
                    onChange={(value) => {
                      form.setFieldsValue({ assigneeUserId: [], teamId: null });
                      setSelectedTeamId(null);
                      fetchTeamsByOrg(value || null);
                    }}
                    className="custom-select"
                  >
                    {assigneeOrgs.map((org) => (
                      <Option key={org.id} value={org.id}>
                        {org.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} sm={24} md={12} lg={12}>
                <Form.Item
                  noStyle
                  shouldUpdate={(prev, cur) => prev.assigneeOrgId !== cur.assigneeOrgId}
                >
                  {() => {
                    const hasOrg = !!form.getFieldValue('assigneeOrgId');
                    return (
                      <Form.Item
                        name="teamId"
                        label={
                          <div>
                            <div>Team</div>
                            <div className="task-assignment-fields__label-hint">
                              Filter assignees by team (optional)
                            </div>
                          </div>
                        }
                        colon={false}
                      >
                        <Select
                          allowClear
                          showSearch
                          placeholder={hasOrg ? 'Select Team (optional)' : 'Select an organization first'}
                          disabled={!hasOrg}
                          loading={teamsLoading}
                          onFocus={() => {
                            const orgId = form.getFieldValue('assigneeOrgId');
                            if (orgId) fetchTeamsByOrg(orgId);
                          }}
                          onSearch={(searchText) => {
                            const orgId = form.getFieldValue('assigneeOrgId');
                            if (orgId) fetchTeamsByOrg(orgId, searchText);
                          }}
                          filterOption={(input, option) =>
                            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                          }
                          onChange={(value) => {
                            setSelectedTeamId(value || null);
                            form.setFieldsValue({ assigneeUserId: [] });
                          }}
                          className="custom-select"
                        >
                          {(teams || []).map((team) => (
                            <Option key={team.id} value={team.id}>
                              {team.name}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    );
                  }}
                </Form.Item>
              </Col>

              <Col xs={24} sm={24} md={12} lg={12}>
                <Form.Item
                  noStyle
                  shouldUpdate={(prev, cur) => prev.assigneeOrgId !== cur.assigneeOrgId}
                >
                  {() => {
                    const hasOrg = !!form.getFieldValue('assigneeOrgId');
                    return (
                      <Form.Item
                        name="assigneeUserId"
                        label={
                          <div>
                            <div>Assignee</div>
                            <div className="task-assignment-fields__label-hint">
                              The person that will receive this task
                            </div>
                          </div>
                        }
                        colon={false}
                        extra={
                          !hasOrg ? (
                            <span className="task-assignment-fields__helper-text">
                              Select an organization first to see available assignees
                            </span>
                          ) : null
                        }
                      >
                        <Select
                          placeholder={hasOrg ? 'Select Assignee' : 'Select an organization first'}
                          showSearch
                          disabled={!hasOrg}
                          loading={basicUsersDetailsLoading}
                          onFocus={() => fetchBasicUsers()}
                          onSearch={(searchText) => fetchBasicUsers(searchText)}
                          filterOption={(input, option) =>
                            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                          }
                          filterSort={(optionA, optionB) =>
                            optionA.children
                              .toLowerCase()
                              .localeCompare(optionB.children.toLowerCase())
                          }
                          onChange={(value) => {
                            const collaborators = currentTask?.collaborators || [];
                            const collaboratorIds = collaborators.map((user) => user.id);
                            const followers = currentTask?.followers || [];
                            const followerIds = followers.map((user) => user.id);
                            if (currentTaskId && collaboratorIds.includes(value)) {
                              setShowCollaboratorModal(true);
                              setSelectedCollaboratorId(value);
                            } else if (currentTaskId && followerIds.includes(value)) {
                              setShowFollowerModal(true);
                              setSelectedFollowerId(value);
                            } else {
                              form.setFieldsValue({
                                assigneeUserId: value,
                              });
                            }
                          }}
                          className="custom-select"
                        >
                          {filteredAssigneeUsers.map((user) => (
                            <Option key={user.id} value={user.id}>
                              {startCase(user.name || user.username)}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    );
                  }}
                </Form.Item>
              </Col>
            </>
          )}
        </Row>

        {/* Row 2: Channel Partner and Advertiser */}
        <Row gutter={32}>
          <Col xs={24} sm={24} md={12} lg={12}>
            <Form.Item
              label={
                <div>
                  <div>Channel Partner</div>
                  <div className="task-assignment-fields__label-hint">
                    The partner this task is regarding
                  </div>
                </div>
              }
              rules={
                level !== 'PLATFORM'
                  ? []
                  : [
                      {
                        required: true,
                        message: 'Channel Partner is Required',
                      },
                    ]
              }
              colon={false}
              name="channelPartnerId"
            >
              <Select
                allowClear
                placeholder={level !== 'PLATFORM' ? userOrgName : 'Select Partner'}
                showSearch
                disabled={level !== 'PLATFORM'}
                loading={basicOrgsDetailsLoading}
                onFocus={() => fetchBasicOrgs('', 'CHANNEL_PARTNER')}
                onSearch={(searchText) => fetchBasicOrgs(searchText, 'CHANNEL_PARTNER')}
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
                filterSort={(optionA, optionB) =>
                  optionA.children
                    .toLowerCase()
                    .localeCompare(optionB.children.toLowerCase())
                }
                onChange={() => {
                  form.setFieldsValue({ advertiserId: [] });
                }}
                className="custom-select"
              >
                {channelPartnerOrgs.map((org) => (
                  <Option key={org.id} value={org.id}>
                    {org.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={12} lg={12}>
            <Form.Item
              name="advertiserId"
              colon={false}
              label={
                <div>
                  <div>Advertiser</div>
                  <div className="task-assignment-fields__label-hint">
                    The advertiser this task is regarding
                  </div>
                </div>
              }
              rules={[{ required: true, message: 'Advertiser is Required' }]}
            >
              <Select
                placeholder="Select Advertiser"
                showSearch
                loading={basicOrgsDetailsLoading}
                onFocus={() => fetchBasicOrgs('', 'ADVERTISER')}
                onSearch={(searchText) => fetchBasicOrgs(searchText, 'ADVERTISER')}
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
                filterSort={(optionA, optionB) =>
                  optionA.children
                    .toLowerCase()
                    .localeCompare(optionB.children.toLowerCase())
                }
                className="custom-select"
              >
                {advertiserOrgs.map((org) => (
                  <Option key={org.id} value={org.id}>
                    {org.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
      </Card>
    </Col>
  );
};

TaskAssignmentFields.propTypes = {
  form: PropTypes.object.isRequired,
  level: PropTypes.string.isRequired,
  currentTaskId: PropTypes.string,
  currentTask: PropTypes.object,
  selectedProjectId: PropTypes.string,
  userOrgName: PropTypes.string,
  assigneeOrgs: PropTypes.array,
  assigneeUsers: PropTypes.array,
  channelPartnerOrgs: PropTypes.array,
  advertiserOrgs: PropTypes.array,
  basicOrgsDetailsLoading: PropTypes.bool,
  basicUsersDetailsLoading: PropTypes.bool,
  fetchBasicOrgs: PropTypes.func.isRequired,
  fetchBasicUsers: PropTypes.func.isRequired,
  setShowCollaboratorModal: PropTypes.func.isRequired,
  setSelectedCollaboratorId: PropTypes.func.isRequired,
  setShowFollowerModal: PropTypes.func.isRequired,
  setSelectedFollowerId: PropTypes.func.isRequired,
  teams: PropTypes.array,
  teamsLoading: PropTypes.bool,
  selectedTeamId: PropTypes.string,
  setSelectedTeamId: PropTypes.func,
  fetchTeamsByOrg: PropTypes.func,
};

TaskAssignmentFields.defaultProps = {
  currentTaskId: null,
  currentTask: null,
  selectedProjectId: null,
  userOrgName: '',
  assigneeOrgs: [],
  assigneeUsers: [],
  channelPartnerOrgs: [],
  advertiserOrgs: [],
  basicOrgsDetailsLoading: false,
  basicUsersDetailsLoading: false,
  teams: [],
  teamsLoading: false,
  selectedTeamId: null,
  setSelectedTeamId: () => {},
  fetchTeamsByOrg: () => {},
};

export default TaskAssignmentFields;
